import i18n from "@/i18n"
import { apiPaths } from "@/lib/api/backendUrls"
import type { DepositFileDownloadResult } from "@/entities/deposit/types"
import { fetchJson } from "@/lib/api/http"
import type { CreativeSelfDefinitionDto } from "@/entities/creative-self-definition/types"
import type { EventParticipantFullProfile, UpdateProfileData, UserProfile } from "@/entities/user-profile/types"
import { deepLeadingLowerKeys, unwrapAbpApplicationResult } from "@/shared/api/abp/normalize"

export const getMyProfile = async (accessToken: string) => {
  return fetchJson<UserProfile>(apiPaths.userProfileMy, {
    method: "GET",
    token: accessToken,
  })
}

/** PDF профиля (все вкладки); ответ как у сертификата депозита — base64 в `content`. */
export const fetchMyProfilePdf = async (accessToken: string): Promise<DepositFileDownloadResult> => {
  const raw = await fetchJson<unknown>(apiPaths.userProfileMyPdf, {
    method: "GET",
    token: accessToken,
    headers: { "Accept-Language": i18n.language || "ru" },
  })
  const body = unwrapAbpApplicationResult(raw)
  return deepLeadingLowerKeys<DepositFileDownloadResult>(body)
}

const EVENT_PARTICIPANT_FULL_PROFILE_TOP_KEYS = [
  "profile",
  "educations",
  "workExperiences",
  "languages",
  "portfolioAchievements",
  "accounts",
  "creativeSelfDefinition",
] as const

/**
 * Newtonsoft часто отдаёт PascalCase с несколькими словами: `WorkExperiences`, `PortfolioAchievements`.
 * `deepLeadingLowerKeys` меняет только первый символ → `workExperiences`, `portfolioAchievements` —
 * это не совпадает с ожидаемыми camelCase-ключами `workExperiences` / `portfolioAchievements`, и поля
 * пропадают при обращении `normalized.workExperiences`.
 */
const readTopLevelCaseInsensitive = (n: Record<string, unknown>, canonicalKey: string): unknown => {
  if (canonicalKey in n) return n[canonicalKey]
  const lower = canonicalKey.toLowerCase()
  const found = Object.keys(n).find((k) => k.toLowerCase() === lower)
  return found !== undefined ? n[found] : undefined
}

const isKnownFullProfileTopKey = (key: string) =>
  EVENT_PARTICIPANT_FULL_PROFILE_TOP_KEYS.some((k) => k.toLowerCase() === key.toLowerCase())

const parseEventParticipantFullProfile = (normalized: Record<string, unknown>): EventParticipantFullProfile => {
  const base: EventParticipantFullProfile = {
    profile: readTopLevelCaseInsensitive(normalized, "profile") as UserProfile,
    educations: readTopLevelCaseInsensitive(normalized, "educations") as EventParticipantFullProfile["educations"],
    workExperiences: readTopLevelCaseInsensitive(
      normalized,
      "workExperiences",
    ) as EventParticipantFullProfile["workExperiences"],
    languages: readTopLevelCaseInsensitive(normalized, "languages") as EventParticipantFullProfile["languages"],
    portfolioAchievements: readTopLevelCaseInsensitive(
      normalized,
      "portfolioAchievements",
    ) as EventParticipantFullProfile["portfolioAchievements"],
    accounts: readTopLevelCaseInsensitive(normalized, "accounts") as EventParticipantFullProfile["accounts"],
    creativeSelfDefinition: readTopLevelCaseInsensitive(
      normalized,
      "creativeSelfDefinition",
    ) as EventParticipantFullProfile["creativeSelfDefinition"],
  }
  const additionalResponse: Record<string, unknown> = {}
  for (const key of Object.keys(normalized)) {
    if (!isKnownFullProfileTopKey(key)) {
      additionalResponse[key] = normalized[key]
    }
  }
  if (Object.keys(additionalResponse).length > 0) {
    return { ...base, additionalResponse }
  }
  return base
}

export const getFullProfileForEventParticipant = async (
  accessToken: string,
  params: { eventId: string; userId: string },
) => {
  const q = new URLSearchParams()
  q.set("eventId", params.eventId)
  q.set("userId", params.userId)
  const raw = await fetchJson<unknown>(`${apiPaths.userProfileFullForEventParticipant}?${q.toString()}`, {
    method: "GET",
    token: accessToken,
  })
  const body = unwrapAbpApplicationResult(raw)
  /** ABP/Newtonsoft часто отдаёт PascalCase — без нормализации массивы и вложенные DTO «пустые» в UI. */
  const normalized = deepLeadingLowerKeys<Record<string, unknown>>(body)
  return parseEventParticipantFullProfile(normalized)
}

export const updateMyProfile = async (accessToken: string, body: UpdateProfileData) => {
  return fetchJson<unknown>(apiPaths.userProfileMy, {
    method: "PUT",
    token: accessToken,
    body,
  })
}

export type ProfileNamesPayload = {
  surname: string
  name: string
  middleName?: string
}

/** После регистрации — только ФИО (как раньше в authApi) */
export const updateMyProfileNames = async (accessToken: string, names: ProfileNamesPayload) => {
  const body: UpdateProfileData = {
    surname: names.surname,
    name: names.name,
  }
  if (names.middleName !== undefined && names.middleName.trim() !== "") {
    body.middleName = names.middleName.trim()
  }
  return updateMyProfile(accessToken, body)
}

export type FileUploadResponse = {
  url?: string
  fileUrl?: string
}

export const uploadFile = async (accessToken: string, file: File) => {
  const formData = new FormData()
  formData.append("file", file)
  const data = await fetchJson<FileUploadResponse | string>(apiPaths.filesUpload, {
    method: "POST",
    token: accessToken,
    body: formData,
  })
  if (typeof data === "string") {
    return { url: data }
  }
  const url = data.url ?? data.fileUrl
  if (!url) {
    throw new Error("Сервер не вернул URL файла")
  }
  return { url }
}
